import {
  DataType,
  Form,
  FormOptions,
  FormSkeleton,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import {
  Box,
  Breadcrumbs,
  Button,
  Paper,
  Typography,
  makeStyles,
  useTheme
} from '@material-ui/core'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  EnumCommodity,
  EnumEstadoCliente,
  EnumServicio,
  EnumTipoPeaje,
  EnumTipoProductoCapacidad,
  EnumTipoSujeto
} from '../../../api'
import { ProductSelectOption } from '../../../api/Argentia/endpoints/contracts/model/contracts'
import { EnumFormaPago } from '../../../api/Argentia/enums/enumFormaPago'
import { EnumPeriodo } from '../../../api/Argentia/enums/periodo'
import {
  ActivityTypeModel,
  CUPSModel,
  DistributorModel,
  PaymentMethodTypeModel,
  PowerModel,
  TollModel
} from '../../../api/Argentia/model/contracts'
import { AccountTableFormat } from '../../../api/Argentia/model/subjects'
import { EnumTipoMetodoPago } from '../../../api/oData/enums/enumTipoMetodoPago'
import Page, { PageLayout } from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { BreadcrumbLink } from '../../../components/shared/breadcrumbLink/breadcrumbLink'
import { enumEnumToValueNumber, enumToSelectOptionArray, enumValueToKey } from '../../../helpers'
import { hasResource } from '../../../helpers/permissionsHelper'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { useConfirmationDialog } from '../../../hooks/useConfirmationDialog'
import { useShowGoToProcedures } from '../../../hooks/useShowGoToProceduresDialog'
import { GlobalNotification, NonAuthRoutes } from '../../../model'
import { Resources } from '../../../model/RBAC/constants'
import { InformationDialogs } from '../../../model/enums/informationDialogs'
import { SearchSelectOption } from '../../../model/searchSelectOption'
import {
  fetchClientContract,
  fetchClientContractsByID,
  fetchClientDataByID,
  getActiveCompany,
  getCustomerDomain,
  notifyError,
  notifyErrorAction
} from '../../../redux'
import {
  selectClientData,
  selectIsFetchingClientData,
  selectIsFetchingClientDataError
} from '../../../redux/selectors/client.selectors'
import {
  getLinkClientText,
  isSubjectCommercial,
  isSubjectCommercialOrWholesaler,
  isSubjectWholesaler
} from '../../clients/client.helper'
import {
  fetchPostalCodes,
  fetchSubjectsByTypeAndCommodity,
  getPostalAddressByID
} from '../../clients/client.service'
import { ProcedureFormMode } from '../../procedures/procedures.types'
import { fetchProductsBySubjectType } from '../../products/products.service'
import {
  accountsToTableFormatMapper,
  listCnaeItemMapper,
  listDistributorItemMapper,
  listTollItemMapper,
  paymentMethodTypesToOptionsMapper
} from '../contracts.mapper'
import {
  ContractDataFormModel,
  ContractsFormValidations,
  RulebookContractsValidations
} from '../contracts.model'
import {
  checkDuplicateContractCode,
  checkValidIBAN,
  fetchATRTypes,
  fetchActivityTypes,
  fetchBillingCycleBySubjectType,
  fetchBooks,
  fetchContractTypes,
  fetchContractsByCups,
  fetchExistingCUPS,
  fetchPaymentMethodsTypes,
  fetchPowerPeriodsByToll,
  fetchPressureGroup,
  fetchServices,
  fetchTolls,
  getClientAccounts,
  saveContract
} from '../contracts.service'
import { styles } from '../contracts.styles'
import { AccountsTableDialog } from '../shared/components/accountsTable.dialog'

export const initialValidations: ContractsFormValidations = {
  isDirty: false,
  startDate: true,
  endDate: true,
  signDate: true,
  contractCode: true,
  iban: true
}

interface ContractAddPageProps {
  location: {
    state: { clientID: number; commodity?: EnumCommodity | null; pathName?: NonAuthRoutes }
  }
}

export const ContractAddPage: React.FC<ContractAddPageProps> = props => {
  const history = useHistory()
  const [t] = useTranslation(['contracts', 'globalerisLibrary', 'enums'])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )
  const { info } = useNotify()

  const clientData = useSelector(selectClientData)
  const isFetchingClient = useSelector(selectIsFetchingClientData)
  const isFetchingClientError = useSelector(selectIsFetchingClientDataError)

  const isCommercialOrWholesale = isSubjectCommercialOrWholesaler(clientData?.type)

  const [contractDataForm, setContractDataForm] = useState<ContractDataFormModel | undefined>({})
  const [pressureGroup, setPressureGroup] = useState<number | string | undefined>()

  const [isFetchingDeps, setIsFetchingDeps] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [productsList, setProductsList] = useState<ProductSelectOption[]>([])
  const [servicesList, setServicesList] = useState<SelectOption[]>([])
  const [tollList, setTollList] = useState<TollModel[]>([])
  const [pressureGroupList, setPressureGroupList] = useState<SelectOption[]>([])
  const [billingCycleList, setBillingCycleList] = useState<SelectOption[]>([])
  const [booksOptions, setBooksOptions] = useState<SelectOption[]>([])
  const [contractTypesOptions, setContractTypesOptions] = useState<SelectOption[]>([])

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodTypeModel[]>([])
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<SelectOption[]>([])
  const [ibanRequired, setIbanRequired] = useState<boolean>(false)

  const [ATRTypesList, setATRTypesList] = useState<SelectOption[]>([])
  const [distributorList, setDistributorList] = useState<DistributorModel[]>([])
  const [powerPeriods, setPowerPeriods] = useState<number>(0)
  const [cnaeList, setCnaeList] = useState<ActivityTypeModel[]>([])
  const [cnaeReadOnly, setCnaeReadOnly] = useState<boolean>(false)

  //TODO - MEJORA:Intentar hacer lo de los periodos de power dinámico (es todo lo que está comentado)
  const [displayP1, setDisplayP1] = useState<boolean>(false)
  const [displayP2, setDisplayP2] = useState<boolean>(false)
  const [displayP3, setDisplayP3] = useState<boolean>(false)
  const [displayP4, setDisplayP4] = useState<boolean>(false)
  const [displayP5, setDisplayP5] = useState<boolean>(false)
  const [displayP6, setDisplayP6] = useState<boolean>(false)
  const [showAddressInputs, setShowAddressInputs] = useState<boolean>(false)

  const [requiredSupplyData, setRequiredSupplyData] = useState<boolean>(false)

  const [clientAccounts, setClientAccounts] = useState<AccountTableFormat[] | undefined>(undefined)
  const [showAccountsTable, setShowAccountsTable] = useState<boolean>(false)

  const clientID = props.location.state.clientID

  const commodity = props.location?.state?.commodity ?? EnumCommodity.GasNatural

  const [validations, setValidations] = useState<ContractsFormValidations>(initialValidations)
  const [startDateMsgError, setStartDateMsgError] = useState<string | undefined>(undefined)
  const [endDateMsgError, setEndDateMsgError] = useState<string | undefined>(undefined)
  const { getInformation, getConfirmation, closeInformation } = useConfirmationDialog()

  const { getShowGoToProceduresDialog } = useShowGoToProcedures()

  const getContractTypes = async (subjectType: EnumTipoSujeto, commodityType: EnumCommodity) => {
    await fetchContractTypes(subjectType, commodityType).then(response => {
      if (response.data !== undefined) {
        setContractTypesOptions(response.data)
      }
    })
  }

  // Hooks Region Starts
  useEffect(() => {
    if (!clientID) {
      notifyErrorAction(t('contracts:forms.add.errors.clientNotFound'))
      history.push(NonAuthRoutes.Client)
    }

    if (!clientData || clientData.id !== clientID) {
      fetchClientDataByID(clientID)
    }
  }, [])

  useEffect(() => {
    if (clientData) {
      const clientType = clientData.type

      if (!clientType) {
        notifyErrorAction(t('contracts:forms.add.errors.clientSubTypeNotValid'))
        return
      }

      const getProducts = async (clientType: EnumTipoSujeto) => {
        await fetchProductsBySubjectType(clientType, commodity).then(response => {
          if (response.data !== undefined) {
            setProductsList(response.data)
          }
        })
      }

      const getServices = async (clientType: EnumTipoSujeto) => {
        await fetchServices(clientType).then(response => {
          if (response.data !== undefined) {
            setServicesList(response.data)
          }
        })
      }

      const getTolls = async (commodityType: EnumCommodity) => {
        await fetchTolls(commodityType).then(response => {
          if (response.data !== undefined) {
            setTollList(response.data)
          }
        })
      }

      const getPressureGroup = async (commodityType: EnumCommodity) => {
        await fetchPressureGroup(commodityType).then(response => {
          if (response.data !== undefined) {
            setPressureGroupList(response.data)
          }
        })
      }

      const getBillingCycle = async (clientType: EnumTipoSujeto) => {
        await fetchBillingCycleBySubjectType(clientType).then(response => {
          if (response.data !== undefined) {
            setBillingCycleList(response.data)
          }
        })
      }

      const getPaymentMethods = async () => {
        await fetchPaymentMethodsTypes().then(response => {
          if (response.data !== undefined) {
            setPaymentMethodOptions(paymentMethodTypesToOptionsMapper(response.data))
            setPaymentMethods(response.data)
          }
        })
      }

      const getATRTypes = async () => {
        await fetchATRTypes().then(response => {
          if (response.data !== undefined) {
            setATRTypesList(response.data)
          }
        })
      }

      const getActivityTypes = async () => {
        await fetchActivityTypes().then(response => {
          if (response.data !== undefined) {
            setCnaeList(response.data)
          }
        })
      }

      const getDistributors = async (commodityType: EnumCommodity) => {
        await fetchSubjectsByTypeAndCommodity(EnumTipoSujeto.Distribuidor, commodityType).then(
          response => {
            if (response.data !== undefined) {
              setDistributorList(response.data)
            }
          }
        )
      }

      const getBooks = async () => {
        await fetchBooks().then(response => {
          if (response !== undefined) {
            setBooksOptions(response)
          }
        })
      }

      Promise.all([
        getProducts(clientType),
        getServices(clientType),
        getTolls(commodity),
        getPressureGroup(commodity),
        getBillingCycle(clientType),
        getPaymentMethods(),
        getDistributors(commodity),
        getActivityTypes(),
        getATRTypes(),
        getBooks()
      ]).then(resp => {
        setIsFetchingDeps(false)
        setContractDataForm(prevState => ({
          ...prevState,
          municipality: clientData.fiscalAddress.municipality,
          municipalityId: clientData.fiscalAddress.municipalityId,
          province: clientData.fiscalAddress.province,
          provinceId: clientData.fiscalAddress.provinceId,
          country: clientData.fiscalAddress.country,
          countryId: clientData.fiscalAddress.countryId,
          postalCode: clientData.fiscalAddress.postalCode,
          postalCodeSearch: {
            value: clientData.fiscalAddress.postalCode,
            label: clientData.fiscalAddress.postalCode
          },
          postalAddress: clientData.fiscalAddress.postalAddress
        }))
      })
      setIsFetchingDeps(true)
    } else if (isFetchingClientError) {
      clientID && fetchClientDataByID(clientID)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientData])

  // Cannot be on any of the other effects because we need both contractData and clientData for this fetch
  useEffect(() => {
    commodity && clientData?.type && getContractTypes(clientData?.type, commodity)
  }, [clientData?.type, commodity])

  const handleFormDirty = (
    validation: RulebookContractsValidations,
    valid: boolean,
    cause?: string
  ) => {
    setValidations(prevValidations => {
      const newValidation = { ...prevValidations, [validation]: valid }
      const isDirty = !(
        newValidation.startDate &&
        newValidation.endDate &&
        newValidation.signDate &&
        newValidation.contractCode &&
        newValidation.iban
      )
      return { ...newValidation, isDirty: isDirty }
    })
  }

  const handlePaymentMethodChange = (paymentMethodId: number) => {
    const paymentMethod = paymentMethods.find(paymentMethod => paymentMethod.Id === paymentMethodId)

    if (paymentMethod && paymentMethod.methodType === EnumFormaPago.Domiciliacion) {
      setIbanRequired(true)
    } else {
      ibanRequired && setIbanRequired(false)
    }
  }

  const GoToClientFile = (clientID: number) => {
    history.push(NonAuthRoutes.ClientFile, {
      clientID: clientID
    })
  }

  const handleClickSave = async () => {
    setIsSaving(true)
    await onSaveContract().then(response => {
      setIsSaving(false)
      if (response.success) {
        fetchClientContractsByID(clientID)

        // If user has Permission and Contract has been created with products, ask if he wants to go to procedures add page
        if (
          contractDataForm &&
          contractDataForm.products &&
          contractDataForm.products.length > 0 &&
          hasResource(Resources.ComunicationsCenterProcedures) &&
          !isSubjectCommercialOrWholesaler(clientData?.type)
        ) {
          getShowGoToProceduresDialog({
            contractID: response.contractID,
            origin:
              commodity === EnumCommodity.Electricidad
                ? ProcedureFormMode.ALTA_CONTRATO_POWER
                : ProcedureFormMode.ALTA_CONTRATO_GAS,
            cancelAction: () => GoToClientFile(clientID)
          })
        } else {
          GoToClientFile(clientID)
        }
      }
    })
  }

  const onSaveContract = async () => {
    let resp = {
      success: false,
      contractID: 0
    }
    if (contractDataForm && clientData) {
      if (missingFieldsFormWithRequiredText(sectionsData, contractDataForm)) {
        notifyError(t('contracts:notifications.create.errorInvalidForm'))
      } else {
        await saveContract(
          {
            ...contractDataForm,
            productsList: contractDataForm.products?.map(function (p) {
              let productCode = productsList.find(x => x.value === p)?.code
              return { id: p, code: productCode! }
            }),
            distributorModel: distributorList.find(x => x.id === contractDataForm.distributor),
            periodsNum: powerPeriods,
            cnaeModel: cnaeList.find(x => x.id === contractDataForm.cnae)
          },
          clientData,
          commodity,
          hasResource(Resources.ComunicationsCenterProcedures) &&
            !isSubjectCommercialOrWholesaler(clientData?.type)
        ).then(response => {
          if (response.success) {
            info(t('contracts:notifications.create.success'))
            resp.success = true
            resp.contractID = response.data.id
          } else {
            notifyError(t('contracts:notifications.create.error'))
          }
        })
      }
    }
    return resp
  }

  const onChangeToll = async (toll: any) => {
    //notDisplayPeriods();
    if (commodity === EnumCommodity.Electricidad && toll) {
      await fetchPowerPeriodsByToll(toll).then(response => {
        if (response.data !== undefined) {
          setPowerPeriods(response.data)
          displayPeriods(response.data)
        }
      })
    }
  }

  const onChangePressure = async () => {
    if (commodity === EnumCommodity.Electricidad) {
      displayPeriods(0)
    }
  }

  const showAnnouncement = async (value: any, customMsg?: string | undefined) => {
    await getInformation(value, customMsg).then(res => {
      closeInformation()
    })
  }

  const handleShowExistingAccounts = async () => {
    if (clientData?.id) {
      await getClientAccounts(clientData?.id).then(response => {
        if (response.success) {
          setClientAccounts(accountsToTableFormatMapper(response.data ?? []))
          setShowAccountsTable(true)
        } else {
          notifyError(t('contracts:notifications.fetchAccounts.error'))
        }
      })
    }
  }

  const handleClickAccountsTable = (account: AccountTableFormat | null) => {
    setContractDataForm(prevState => ({ ...prevState, useExistingAccount: false }))
    if (account) {
      setContractDataForm(prevState => ({
        ...prevState,
        paymentMethod: enumEnumToValueNumber(EnumTipoMetodoPago, account.paymentMethod.type),
        iban: account.paymentMethod.iban,
        billingCycle: account.billingCycleId,
        municipality: account.paymentMethod.address.municipality,
        province: account.paymentMethod.address.province,
        country: account.paymentMethod.address.country,
        municipalityId: account.paymentMethod.address.municipalityId,
        provinceId: account.paymentMethod.address.provinceId,
        countryId: account.paymentMethod.address.countryId,
        postalCode: account.paymentMethod.address.postalCode,
        postalCodeSearch: {
          value: account.paymentMethod.address.postalCode,
          label: account.paymentMethod.address.postalCode
        },
        postalAddress: account.paymentMethod.address.postalAddress
      }))
      setShowAddressInputs(true)
    }
    setShowAccountsTable(false)
  }

  const updateAddress = async (option: SearchSelectOption | null) => {
    if (!option) {
      setContractDataForm(prevState => ({
        ...prevState,
        municipalityId: undefined,
        municipality: '',
        provinceId: undefined,
        province: '',
        countryId: undefined,
        country: '',
        postalCode: ''
      }))
      return
    }

    const postalAddress = await getPostalAddressByID(+option.value).catch(e => {
      console.log(e)
    })

    if (postalAddress) {
      setContractDataForm(prevState => ({
        ...prevState,
        municipalityId: postalAddress.municipality.id,
        municipality: postalAddress.municipality.description,
        provinceId: postalAddress.province.id,
        province: postalAddress.province.description,
        countryId: postalAddress.country.id,
        country: postalAddress.country.description,
        postalCode: option.textValue
      }))
    }
  }

  // Hooks Region Ends

  //Functions Region Starts
  const getExistingCUPS = async (cups: string) => {
    if (contractDataForm?.startDate == undefined || contractDataForm?.endDate == undefined) {
      setContractDataForm(prevState => ({
        ...prevState,
        cups: undefined
      }))
      showAnnouncement(InformationDialogs.CONTRACT_DATES_NOT_SELECTED)
    } else {
      setIsFetchingDeps(true)
      info(t('contracts:notifications.create.findingCUPS'))
      await fetchExistingCUPS(cups, commodity).then(async response => {
        if (response.success && response.data?.exists) {
          const activeCompany = getActiveCompany()
          await fetchContractsByCups(
            cups,
            activeCompany?.id!,
            contractDataForm?.startDate!,
            contractDataForm?.endDate
          ).then(async responseContracts => {
            if (responseContracts.data && responseContracts.data.length > 0) {
              const activeContracts = responseContracts.data.filter(
                x =>
                  enumEnumToValueNumber(EnumEstadoCliente, x.status) != EnumEstadoCliente.Baja &&
                  enumEnumToValueNumber(EnumEstadoCliente, x.status) !=
                    EnumEstadoCliente.Inactivo &&
                  enumEnumToValueNumber(EnumEstadoCliente, x.status) != EnumEstadoCliente.Anulado
              )
              if (activeContracts && activeContracts.length > 0) {
                const contractsCodes = activeContracts.map(x => x.contractCode).join(',')
                showAnnouncement(
                  InformationDialogs.CONTRACT_ACTIVE_CUPS_ALREADY_ASSOCIATED,
                  t('informationDialogs:CONTRACT_ACTIVE_CUPS_ALREADY_ASSOCIATED.message', {
                    contracts: contractsCodes
                  })
                )
              } else {
                const inactiveContracts = responseContracts.data.filter(
                  x =>
                    enumEnumToValueNumber(EnumEstadoCliente, x.status) === EnumEstadoCliente.Baja ||
                    enumEnumToValueNumber(EnumEstadoCliente, x.status) ===
                      EnumEstadoCliente.Inactivo ||
                    enumEnumToValueNumber(EnumEstadoCliente, x.status) === EnumEstadoCliente.Anulado
                )
                const contractsCodes = inactiveContracts.map(x => x.contractCode).join(',')
                showAnnouncement(
                  InformationDialogs.CONTRACT_INACTIVE_CUPS_ALREADY_ASSOCIATED,
                  t('informationDialogs:CONTRACT_INACTIVE_CUPS_ALREADY_ASSOCIATED.message', {
                    contracts: contractsCodes
                  })
                )
              }
              setContractDataForm(prevState => ({
                ...prevState,
                cups: undefined,
                distributor: undefined,
                telemetered: undefined,
                service: undefined,
                pressureGroup: undefined,
                toll: undefined,
                cnae: undefined,
                qaEstimated: undefined,
                qdHired: undefined
              }))

              checkRequiredSupplyData()
            } else {
              setContractData(response.data!)
              checkRequiredSupplyData()
              info(t('contracts:notifications.create.existingCUPS'))
            }
          })
        } else {
          if (commodity === EnumCommodity.Electricidad) {
            setContractDataForm(prevState => ({
              ...prevState,
              period1: undefined,
              period2: undefined,
              period3: undefined,
              period4: undefined,
              period5: undefined,
              period6: undefined
            }))
          }
          setContractDataForm(prevState => ({
            ...prevState,
            cups: cups
          }))
          setCnaeReadOnly(false)
          info(t('contracts:notifications.create.notExistingCUPS'))
        }
        setIsFetchingDeps(false)
      })
    }
  }

  const setContractData = async (data: CUPSModel) => {
    if (commodity === EnumCommodity.GasNatural) {
      setContractDataFormGas(data)
    } else {
      setContractDataFormPower(data)
    }
    setCnaeReadOnly(true)
  }

  const setContractDataFormGas = async (data: CUPSModel) => {
    const capacity =
      data.capacitiesList && data.capacitiesList.length > 0
        ? data.capacitiesList[data.capacitiesList.length - 1]
        : undefined

    if (capacity) {
      let capacityToll = capacity.toll
      let pressureGroup = data.pressureGroupId
      setPressureGroup(pressureGroup)

      setContractDataForm(prevState => ({
        ...prevState,
        telemetered: data.telemetered,
        exists: true,
        cnae: data.cnae ? cnaeList.find(x => x.code === data.cnae)?.id : contractDataForm?.cnae,
        service: capacity.contractType
          ? enumValueToKey(EnumServicio, capacity.contractType)
          : contractDataForm?.service,
        pressureGroup: pressureGroup,
        toll: enumValueToKey(EnumTipoPeaje, capacityToll),
        distributor: /*?*/ distributorList.find(
          x => x.description === data.distributor?.description
        )?.id,
        qaEstimated: capacity.qaEstimada,
        qdHired: capacity.qdContratada
      }))
    }
  }

  const setContractDataFormPower = async (data: CUPSModel) => {
    const contractATR =
      data.contractATRList && data.contractATRList.length > 0
        ? data.contractATRList[data.contractATRList.length - 1]
        : undefined

    if (contractATR) {
      const version =
        contractATR.versions && contractATR.versions.length > 0
          ? contractATR.versions[contractATR.versions.length - 1]
          : undefined

      if (version) {
        const atrToll = version.tollType!
        await fetchPowerPeriodsByToll(atrToll).then(response => {
          if (response.data !== undefined) {
            setPowerPeriods(response.data)
          }
        })
        let tensionGroup = data.pressureGroupId
        setPressureGroup(tensionGroup)

        setContractDataForm(prevState => ({
          ...prevState,
          telemetered: data.telemetered,
          exists: true,
          cnae: data.cnae ? cnaeList.find(x => x.code === data.cnae)?.id : contractDataForm?.cnae,
          pressureGroup: tensionGroup,
          toll: atrToll,
          distributor: distributorList.find(x => x.description === data.distributor?.description)
            ?.id,
          period1: getPowerPeriodToSet(version.powers, EnumPeriodo.Periodo1),
          period2: getPowerPeriodToSet(version.powers, EnumPeriodo.Periodo2),
          period3: getPowerPeriodToSet(version.powers, EnumPeriodo.Periodo3),
          period4: getPowerPeriodToSet(version.powers, EnumPeriodo.Periodo4),
          period5: getPowerPeriodToSet(version.powers, EnumPeriodo.Periodo5),
          period6: getPowerPeriodToSet(version.powers, EnumPeriodo.Periodo6),
          aTRCode:
            data.contractATRList && data.contractATRList.length > 0
              ? data.contractATRList[0].code
              : undefined,
          aTRType:
            data.contractATRList && data.contractATRList.length > 0
              ? data.contractATRList[0].type
              : undefined
        }))
      }
    }
  }

  function getPowerPeriodToSet(powers: PowerModel[], periodEnum: EnumPeriodo) {
    let currentPeriod = powers.find(x => x.period === periodEnum)
    let power: number | undefined = undefined
    let displayPeriod = false
    if (currentPeriod) {
      power = currentPeriod.value
      displayPeriod = true
    }

    switch (periodEnum) {
      case EnumPeriodo.Periodo1:
        setDisplayP1(displayPeriod)
        break
      case EnumPeriodo.Periodo2:
        setDisplayP2(displayPeriod)
        break
      case EnumPeriodo.Periodo3:
        setDisplayP3(displayPeriod)
        break
      case EnumPeriodo.Periodo4:
        setDisplayP4(displayPeriod)
        break
      case EnumPeriodo.Periodo5:
        setDisplayP5(displayPeriod)
        break
      case EnumPeriodo.Periodo6:
        setDisplayP6(displayPeriod)
        break
    }

    return power
  }

  //TODO: Se que esto no es lo adecuado. Ver cómo cambiarlo
  //He dejado esto porqué no se otra forma de hacerlo, lo ideal sería hacerlo dinámico
  //pero no lo he conseguido. Lo dejo de esta forma porque de cara al usuario queda más limpio
  //el efecto que hace
  const displayPeriods = async (numPeriods: number) => {
    if (numPeriods === 1) {
      setDisplayP1(true)
      setDisplayP2(false)
      setDisplayP3(false)
      setDisplayP4(false)
      setDisplayP5(false)
      setDisplayP6(false)
    } else if (numPeriods === 2) {
      setDisplayP1(true)
      setDisplayP2(true)
      setDisplayP3(false)
      setDisplayP4(false)
      setDisplayP5(false)
      setDisplayP6(false)
    } else if (numPeriods === 3) {
      setDisplayP1(true)
      setDisplayP2(true)
      setDisplayP3(true)
      setDisplayP4(false)
      setDisplayP5(false)
      setDisplayP6(false)
    } else if (numPeriods === 4) {
      setDisplayP1(true)
      setDisplayP2(true)
      setDisplayP3(true)
      setDisplayP4(true)
      setDisplayP5(false)
      setDisplayP6(false)
    } else if (numPeriods === 5) {
      setDisplayP1(true)
      setDisplayP2(true)
      setDisplayP3(true)
      setDisplayP4(true)
      setDisplayP5(true)
      setDisplayP6(false)
    } else if (numPeriods === 6) {
      setDisplayP1(true)
      setDisplayP2(true)
      setDisplayP3(true)
      setDisplayP4(true)
      setDisplayP5(true)
      setDisplayP6(true)
    } else {
      setDisplayP1(false)
      setDisplayP2(false)
      setDisplayP3(false)
      setDisplayP4(false)
      setDisplayP5(false)
      setDisplayP6(false)
    }

    setContractDataForm(prevState => ({
      ...contractDataForm,
      period1: undefined,
      period2: undefined,
      period3: undefined,
      period4: undefined,
      period5: undefined,
      period6: undefined
    }))
  }

  function checkRequiredSupplyData() {
    const isRequired =
      (contractDataForm?.cups != undefined && contractDataForm?.cups.length > 0) ||
      (contractDataForm?.aTRCode && contractDataForm?.aTRCode.length > 0) ||
      (contractDataForm?.qdHired != undefined && contractDataForm?.qdHired.toString.length > 0) ||
      (contractDataForm?.qaEstimated != undefined &&
        contractDataForm?.qaEstimated.toString.length > 0) ||
      contractDataForm?.cnae != undefined ||
      contractDataForm?.distributor != undefined ||
      contractDataForm?.aTRType != undefined ||
      contractDataForm?.toll != undefined ||
      contractDataForm?.pressureGroup != undefined ||
      contractDataForm?.service != undefined ||
      contractDataForm?.telemetered == true ||
      (contractDataForm?.products != undefined && contractDataForm?.products?.length > 0)

    setRequiredSupplyData(isRequired)
  }

  //Functions Region Ends

  //Form config Starts
  const options: FormOptions = {
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: t('contracts:forms.add.analytics')
    }
  }

  const handleNewCode = async (code: string) => {
    const response = await checkDuplicateContractCode(code)
    if (response.success && response.data) {
      if (response.data.isValid) {
        handleFormDirty('contractCode', true)
      } else {
        handleFormDirty('contractCode', false)
      }
    }
  }

  const handleNewIban = async (iban: string) => {
    if (iban === '') {
      handleFormDirty('iban', true)
    } else {
      const response = await checkValidIBAN(iban)
      if (response.success && response.data) {
        if (response.data.isValid) {
          handleFormDirty('iban', true)
        } else {
          handleFormDirty('iban', false)
        }
      }
    }
  }

  const installationDataSection = {
    title: t('contracts:forms.add.titles.installationData'),
    inputs: [
      {
        name: 'cups',
        label: t('contracts:forms.add.inputs.cups'),
        helpText: t('contracts:notifications.help.dates'),
        type: DataType.TEXT,
        required: requiredSupplyData,
        clearable: true,
        onBlur: async value => {
          value && getExistingCUPS(value.toString())
          checkRequiredSupplyData()
        }
      },
      {
        name: 'distributor',
        label: t('contracts:forms.add.inputs.distributor'),
        type: DataType.SELECT,
        selectOptions: listDistributorItemMapper(distributorList),
        required: requiredSupplyData,
        clearable: true,
        onChange: value => {
          checkRequiredSupplyData()
        }
      },
      {
        name: 'telemetered',
        label: t('contracts:forms.add.inputs.telemetered'),
        type: DataType.SWITCH,
        onChange: value => {
          checkRequiredSupplyData()
        }
      },
      {
        name: 'pressureGroup',
        label: t('contracts:forms.add.inputs.pressureGroup'),
        type: DataType.SELECT,
        selectOptions: pressureGroupList,
        required: requiredSupplyData,
        clearable: true,
        onChange: (value, formState: any) => {
          setPressureGroup(value)
          onChangePressure()
          checkRequiredSupplyData()
          //Al cambiar el grupo de presión se elimina el peaje y el servicio seleccionados previamente
          setContractDataForm(prevState => ({
            ...prevState,
            service: undefined,
            toll: undefined
          }))
        }
      },
      {
        name: 'service',
        label: t('contracts:forms.add.inputs.service'),
        type: DataType.SELECT,

        selectOptions: servicesList!,
        required: commodity === EnumCommodity.GasNatural && requiredSupplyData,
        display: commodity === EnumCommodity.GasNatural,
        clearable: true,
        helpText: t('contracts:notifications.help.pressureGroup'),
        onChange: value => {
          checkRequiredSupplyData()
        }
      },
      {
        name: 'toll',
        label: t('contracts:forms.add.inputs.toll'),
        type: DataType.SELECT,
        selectOptions: pressureGroup
          ? listTollItemMapper(
              tollList!.filter(x => x.pressureGroupList.findIndex(x => x === pressureGroup) > -1)
            )
          : listTollItemMapper(tollList!),
        required: requiredSupplyData,
        clearable: true,
        helpText: t('contracts:notifications.help.pressureGroup'),
        onChange: (value, formState: any) => {
          onChangeToll(value)
          checkRequiredSupplyData()
        }
      },
      {
        name: 'contractCapacityType',
        label: t('contracts:components.capacitiesTable.columns.contractCapacityType'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(
          EnumTipoProductoCapacidad,
          'enums:EnumTipoProductoCapacidad'
        ),
        display: commodity === EnumCommodity.GasNatural
      },
      {
        name: 'cnae',
        label: t('contracts:forms.add.inputs.cnae'),
        type: DataType.SELECT,
        selectOptions: listCnaeItemMapper(cnaeList),
        required: false,
        readOnly: cnaeReadOnly,
        clearable: true,
        onChange: value => {
          checkRequiredSupplyData()
        }
      },
      {
        name: 'qaEstimated',
        label: t('contracts:forms.add.inputs.qaEstimated'),
        suffix: t('contracts:forms.add.units.kWh'),
        type: DataType.NUMBER,
        // required: commodity === EnumCommodity.GasNatural,
        display: commodity === EnumCommodity.GasNatural,
        onBlur: value => {
          checkRequiredSupplyData()
        }
      },
      {
        name: 'qdHired',
        label: t('contracts:forms.add.inputs.qdHired'),
        suffix: t('contracts:forms.add.units.kWh'),
        type: DataType.NUMBER,
        display: commodity === EnumCommodity.GasNatural,
        required: commodity === EnumCommodity.GasNatural && requiredSupplyData,
        onBlur: value => {
          checkRequiredSupplyData()
        }
      },
      {
        name: 'aTRCode',
        label: t('contracts:forms.add.inputs.ATRCode'),
        type: DataType.TEXT,
        required: commodity === EnumCommodity.Electricidad && requiredSupplyData,
        display: commodity === EnumCommodity.Electricidad,
        onBlur: value => {
          setContractDataForm(prevState => ({
            ...prevState,
            aTRCode: value ? `${value}` : undefined
          }))
          checkRequiredSupplyData()
        }
      },
      {
        name: 'aTRType',
        label: t('contracts:forms.add.inputs.ATRType'),
        type: DataType.SELECT,
        selectOptions: ATRTypesList,
        required: commodity === EnumCommodity.Electricidad && requiredSupplyData,
        display: commodity === EnumCommodity.Electricidad,
        clearable: true,
        onChange: value => {
          setContractDataForm(prevState => ({
            ...prevState,
            aTRType: +value
          }))
          checkRequiredSupplyData()
        }
      },
      {
        name: 'period1',
        label: t('contracts:forms.add.inputs.period1'),
        suffix: t('contracts:forms.add.units.kW'),
        type: DataType.NUMBER,
        customDecimalScale: 3,
        required: commodity === EnumCommodity.Electricidad && displayP1 && requiredSupplyData,
        display: commodity === EnumCommodity.Electricidad && displayP1,
        onChange: value => {
          setContractDataForm(prevState => ({
            ...prevState,
            period1: +value
          }))
        }
      },
      {
        name: 'period2',
        label: t('contracts:forms.add.inputs.period2'),
        suffix: t('contracts:forms.add.units.kW'),
        type: DataType.NUMBER,
        customDecimalScale: 3,
        required: commodity === EnumCommodity.Electricidad && displayP2 && requiredSupplyData,
        display: commodity === EnumCommodity.Electricidad && displayP2,
        onChange: value => {
          setContractDataForm(prevState => ({
            ...prevState,
            period2: +value
          }))
        }
      },
      {
        name: 'period3',
        label: t('contracts:forms.add.inputs.period3'),
        suffix: t('contracts:forms.add.units.kW'),
        type: DataType.NUMBER,
        customDecimalScale: 3,
        required: commodity === EnumCommodity.Electricidad && displayP3 && requiredSupplyData,
        display: commodity === EnumCommodity.Electricidad && displayP3,
        onChange: value => {
          setContractDataForm(prevState => ({
            ...prevState,
            period3: +value
          }))
        }
      },
      {
        name: 'period4',
        label: t('contracts:forms.add.inputs.period4'),
        suffix: t('contracts:forms.add.units.kW'),
        type: DataType.NUMBER,
        customDecimalScale: 3,
        required: commodity === EnumCommodity.Electricidad && displayP4 && requiredSupplyData,
        display: commodity === EnumCommodity.Electricidad && displayP4,
        onChange: value => {
          setContractDataForm(prevState => ({
            ...prevState,
            period4: +value
          }))
        }
      },
      {
        name: 'period5',
        label: t('contracts:forms.add.inputs.period5'),
        suffix: t('contracts:forms.add.units.kW'),
        type: DataType.NUMBER,
        customDecimalScale: 3,
        required: commodity === EnumCommodity.Electricidad && displayP5 && requiredSupplyData,
        display: commodity === EnumCommodity.Electricidad && displayP5,
        onChange: value => {
          setContractDataForm(prevState => ({
            ...prevState,
            period5: +value
          }))
        }
      },
      {
        name: 'period6',
        label: t('contracts:forms.add.inputs.period6'),
        suffix: t('contracts:forms.add.units.kW'),
        type: DataType.NUMBER,
        customDecimalScale: 3,
        required: commodity === EnumCommodity.Electricidad && displayP6 && requiredSupplyData,
        display: commodity === EnumCommodity.Electricidad && displayP6,
        onChange: value => {
          setContractDataForm(prevState => ({
            ...prevState,
            period6: +value
          }))
        }
      }
    ]
  }

  let sectionsData: Section[] = [
    {
      title: t('contracts:forms.add.titles.contract'),
      inputs: [
        {
          name: 'contractType',
          label: t('contracts:forms.add.inputs.contractType'),
          selectOptions: contractTypesOptions,
          type: DataType.SELECT,
          required: true
        },
        {
          name: 'contractCode',
          label: t('contracts:forms.add.inputs.contractCode'),
          type: DataType.TEXT,
          required: true,
          errorState: !validations.contractCode,
          errorText: t('contracts:notifications.errors.contractCode'),
          onBlur: code => {
            handleNewCode(`${code}`)
            setContractDataForm(prevState => ({ ...prevState, contractCode: `${code}` }))
          }
        },
        {
          name: 'products',
          label: t('contracts:forms.add.inputs.products'),
          type: DataType.MULTISELECT,
          selectOptions: productsList,
          required: requiredSupplyData,
          onChange: value => {
            checkRequiredSupplyData()
          }
        },
        {
          name: 'startDate',
          label: t('contracts:forms.add.inputs.startDate'),
          type: DataType.DATE,
          required: true,
          errorState: !validations.startDate,
          errorText: !validations.startDate ? t('contracts:notifications.errors.startDate') : '',
          onBlur: async (newStartDate: any) => {
            if (
              contractDataForm?.endDate &&
              moment(contractDataForm?.endDate).isBefore(moment(newStartDate), 'days')
            ) {
              handleFormDirty(RulebookContractsValidations.START_DATE, false, 'tradeStartDate')
              return
            } else {
              handleFormDirty(RulebookContractsValidations.START_DATE, true)
              handleFormDirty(RulebookContractsValidations.END_DATE, true)
            }
            // Either in Create or Edit mode, we check if sign date is valid for the new start date:
            if (
              contractDataForm?.signDate &&
              moment(contractDataForm?.signDate).isAfter(moment(newStartDate), 'days')
            ) {
              handleFormDirty(RulebookContractsValidations.SIGN_DATE, false)
            } else {
              handleFormDirty(RulebookContractsValidations.SIGN_DATE, true)
            }
            if (contractDataForm?.cups !== undefined && contractDataForm?.cups !== '') {
              getExistingCUPS(contractDataForm?.cups.toString())
              checkRequiredSupplyData()
            }
          }
        },
        {
          name: 'endDate',
          label: t('contracts:forms.add.inputs.endDate'),
          type: DataType.DATE,
          required: !isSubjectCommercialOrWholesaler(clientData?.type), // indefinite endDate for commercial or wholesaler
          errorState: !validations.endDate,
          errorText: !validations.endDate ? t('contracts:notifications.errors.endDate') : '',
          onBlur: async (newEndDate: any) => {
            if (
              contractDataForm?.startDate &&
              moment(newEndDate).isBefore(moment(contractDataForm?.startDate), 'days')
            ) {
              handleFormDirty(RulebookContractsValidations.END_DATE, false, 'tradeEndDate')
            } else {
              startDateMsgError === 'tradeStartDate' &&
                handleFormDirty(RulebookContractsValidations.START_DATE, true)
              handleFormDirty(RulebookContractsValidations.END_DATE, true)
            }
            if (contractDataForm?.cups != undefined && contractDataForm?.cups != '') {
              getExistingCUPS(contractDataForm?.cups.toString())
              checkRequiredSupplyData()
            }
          }
        },
        {
          name: 'signDate',
          label: t('contracts:forms.add.inputs.signDate'),
          type: DataType.DATE,
          required: true,
          errorState: !validations.signDate,
          errorText: !validations.signDate ? t('contracts:notifications.errors.signDate') : '',
          onBlur: async (value: any) => {
            if (
              value &&
              contractDataForm?.startDate &&
              moment(value).isAfter(moment(contractDataForm?.startDate), 'days')
            ) {
              handleFormDirty(RulebookContractsValidations.SIGN_DATE, false)
            } else {
              handleFormDirty(RulebookContractsValidations.SIGN_DATE, true)
            }
          }
        }
      ]
    },
    {
      title: t('contracts:forms.add.titles.billingData'),
      inputs: [
        {
          name: 'billingCycle',
          label: t('contracts:forms.add.inputs.billingCycle'),
          type: DataType.SELECT,
          selectOptions: billingCycleList,
          required: true,
          onChange: value => {
            setContractDataForm(prevState => ({
              ...prevState,
              billingCycle: +value
            }))
          }
        },
        {
          name: 'paymentMethod',
          label: t('contracts:forms.add.inputs.paymentMethod'),
          type: DataType.SELECT,
          selectOptions: paymentMethodOptions,
          required: true,
          onChange: newMethod => {
            setContractDataForm(prevState => ({
              ...prevState,
              paymentMethod: +newMethod
            }))
            handlePaymentMethodChange(+newMethod)
          }
        },
        {
          name: 'iban',
          label: t('contracts:forms.add.inputs.iban'),
          type: DataType.TEXT,
          errorState: !validations.iban,
          errorText: t('contracts:notifications.errors.iban'),
          required: ibanRequired,
          onBlur: iban => {
            handleNewIban(`${iban}`)
            setContractDataForm(prevState => ({ ...prevState, iban: `${iban}` }))
          }
        },
        {
          name: 'postalAddress',
          label: t('contracts:forms.edit.inputs.street'),
          type: DataType.TEXT,
          required: true,
          display: showAddressInputs,
          onBlur: address => {
            setContractDataForm(prevState => ({ ...prevState, postalAddress: `${address}` }))
          }
        },
        {
          name: 'postalCodeSearch',
          label: t('contracts:forms.edit.inputs.postalCode'),
          type: DataType.SEARCHFIELD,
          apiCall: fetchPostalCodes,
          maxLength: 5,
          required: true,
          clearable: true,
          display: showAddressInputs,
          onClear: () => {
            updateAddress(null)
          },
          onChange: newCode => {
            const option = newCode as any
            updateAddress(option)
          }
        },
        {
          name: 'municipality',
          label: t('contracts:forms.edit.inputs.municipality'),
          type: DataType.TEXT,
          required: true,
          readOnly: true,
          display: showAddressInputs
        },
        {
          name: 'province',
          label: t('contracts:forms.edit.inputs.province'),
          type: DataType.TEXT,
          required: true,
          readOnly: true,
          display: showAddressInputs
        },
        {
          name: 'country',
          label: t('contracts:forms.edit.inputs.country'),
          type: DataType.TEXT,
          required: true,
          readOnly: true,
          display: showAddressInputs
        },
        {
          name: 'useExistingAccount',
          label: t('contracts:forms.add.inputs.useExistingAccount'),
          type: DataType.SWITCH,
          readOnly: !clientData?.id,
          onChange: value => {
            if (value) {
              handleShowExistingAccounts()
            }
          }
        }
      ]
    },
    {
      title: t('contracts:forms.add.titles.aditionals'),
      inputs: [
        {
          name: 'description',
          label: t('contracts:forms.add.inputs.contractDescription'),
          type: DataType.TEXT
        },
        {
          name: 'book',
          label: t('contracts:forms.add.inputs.book'),
          type: DataType.SELECT,
          clearable: true,
          isLoading: isFetchingDeps,
          selectOptions: booksOptions,
          display: !isSubjectCommercial(clientData?.type) // Show only if not commercial
        },
        {
          name: 'orderNm',
          label: t('contracts:forms.add.inputs.orderNumber'),
          type: DataType.TEXT
        }
      ],
      collapsible: true
    }
  ]

  const pageTitle = isSubjectCommercial(clientData?.type)
    ? t('contracts:titleAddCommercial')
    : isSubjectWholesaler(clientData?.type)
      ? t('contracts:titleAddWholesaler')
      : commodity === EnumCommodity.Electricidad
        ? t('contracts:titleAddPower')
        : commodity === EnumCommodity.GasNatural
          ? t('contracts:titleAddGas')
          : t('contracts:titleAdd')

  if (!isCommercialOrWholesale) {
    sectionsData.splice(1, 0, installationDataSection)
  }

  //Form config Ends

  return (
    <Page notification={globalNotification} fullWidth={false} layout={PageLayout.SINGLE_COLUMN}>
      <PageHeader title={pageTitle}>
        <Button
          variant="text"
          color="primary"
          size="small"
          disabled={isSaving || isFetchingClient || isFetchingDeps}
          onClick={() => {
            if (props.location.state.pathName === NonAuthRoutes.ClientFile) {
              history.goBack()
            } else if (clientData?.id) {
              history.push(NonAuthRoutes.ClientFile, { clientID: clientData.id })
            } else {
              history.push(NonAuthRoutes.Client)
            }
          }}
        >
          {t('contracts:buttonsAdd.cancel')}
        </Button>
        <Button
          variant="text"
          color="primary"
          size="small"
          disabled={isSaving || isFetchingClient || isFetchingDeps || validations.isDirty}
          onClick={async () => {
            setIsSaving(true)
            await onSaveContract().then(response => {
              setIsSaving(false)
              if (response.success && clientData && contractDataForm) {
                fetchClientContract(response.contractID)
                history.push(NonAuthRoutes.AddressInfo, {
                  clientID: clientID,
                  contractID: response.contractID,
                  documentID: clientData.documentID,
                  contractCode: contractDataForm?.contractCode
                })
              }
            })
          }}
        >
          {t('contracts:buttonsAdd.editAddresses')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={isSaving || isFetchingClient || isFetchingDeps || validations.isDirty}
          onClick={handleClickSave}
        >
          {t('contracts:buttonsAdd.save')}
        </Button>
      </PageHeader>
      {isFetchingClient || isFetchingDeps ? (
        <FormSkeleton withTitle={true} elevation={0} columns={1} inputs={9}></FormSkeleton>
      ) : (
        <>
          <Box mb={1}>
            <Breadcrumbs separator=">" aria-label="breadcrumb">
              {clientData && (
                <BreadcrumbLink toPathName="/client/file" state={{ clientID: clientData?.id }}>
                  {`Cliente ${getLinkClientText(clientData)} - ID ${clientData.documentID}`}
                </BreadcrumbLink>
              )}
              <Typography key="3" color="primary" variant="subtitle1">
                {t('contracts:titleAdd')}
              </Typography>
            </Breadcrumbs>
          </Box>
          <Paper elevation={6}>
            <Form
              title={t('contracts:formTitle')}
              sections={sectionsData}
              data={contractDataForm}
              options={options}
            />
          </Paper>
        </>
      )}
      <AccountsTableDialog
        show={showAccountsTable}
        accounts={clientAccounts}
        onHandleClick={handleClickAccountsTable}
        paymentMethodOptions={paymentMethodOptions}
        billingCycleOptions={billingCycleList}
      />
    </Page>
  )
}
